import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Save, Instagram, Facebook, Youtube, Mail, Phone, Smartphone, Music } from 'lucide-react';
import ImageUploader from '../../components/admin/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Paramètres sauvegardés!');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const settingSections = [
    {
      title: 'Contact',
      icon: Mail,
      fields: [
        { key: 'contact_email', label: 'Email', type: 'email', icon: Mail },
        { key: 'whatsapp_number', label: 'WhatsApp', type: 'tel', icon: Phone },
      ]
    },
    {
      title: 'Réseaux sociaux',
      icon: Instagram,
      fields: [
        { key: 'instagram_url', label: 'Instagram', type: 'url', icon: Instagram },
        { key: 'facebook_url', label: 'Facebook', type: 'url', icon: Facebook },
        { key: 'tiktok_url', label: 'TikTok', type: 'url' },
        { key: 'youtube_url', label: 'YouTube', type: 'url', icon: Youtube },
      ]
    },
    {
      title: 'Application mobile',
      icon: Smartphone,
      fields: [
        { key: 'app_store_url', label: 'App Store URL', type: 'url' },
        { key: 'google_play_url', label: 'Google Play URL', type: 'url' },
      ]
    },
    {
      title: 'Billetterie',
      icon: null,
      fields: [
        { key: 'xceed_openair_url', label: 'Xceed Open Air', type: 'url' },
        { key: 'xceed_mirano_url', label: 'Xceed Mirano', type: 'url' },
      ]
    },
    {
      title: 'Musique',
      icon: Music,
      fields: [
        { key: 'spotify_playlist_url', label: 'Spotify Playlist URL (ex: https://open.spotify.com/playlist/XXXX)', type: 'url', icon: Music },
      ]
    },
  ];

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Paramètres</h1>
          <p className="text-sm text-[#B9B2A8]">Configurez les informations générales</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      <div className="space-y-6">
        {settingSections.map((section, index) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/5 border border-white/10 rounded-xl p-6"
          >
            <h2 className="font-display text-lg text-[#F5F0E8] mb-4 flex items-center gap-2">
              {section.icon && <section.icon className="w-5 h-5 text-[#C9A84C]" />}
              {section.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                    {field.label}
                  </label>
                  <div className="relative">
                    {field.icon && (
                      <field.icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B9B2A8]" />
                    )}
                    <Input
                      type={field.type}
                      value={settings[field.key] || ''}
                      onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
                      className={`bg-white/5 border-white/10 text-[#F5F0E8] ${field.icon ? 'pl-10' : ''}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}

        {/* App screenshot uploader (shown in phone mockup on home) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#C9A84C]" />
            Screenshot de l'app (mockup téléphone home)
          </h2>
          <ImageUploader
            label="Image affichée dans le mockup iPhone de la section App"
            value={settings.app_screenshot_url}
            onChange={(url) => setSettings({ ...settings, app_screenshot_url: url })}
          />
          <p className="text-xs text-[#B9B2A8] mt-2">
            Format vertical recommandé (9:19). Ratio iPhone. Upload un screenshot de ton app.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
